import type { ReactNode } from 'react'
import type { SiteSettings } from '@/types'

import Link from 'next/link'
import { Instagram } from 'lucide-react'
import { useT } from '@/lib/i18n'
import { Logo } from '@/components/generals/logo'
import { Cms } from '@/components/generals/cms'

interface FooterProps {
    settings?: SiteSettings | null
}

export const Footer = ({ settings }: FooterProps): ReactNode => {
    const t = useT()

    return (
        <footer id='site-footer' className='border-t-2 border-line bg-ink text-white'>
            <div className='mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-[2fr_1fr_1fr]'>
                <div className='max-w-sm'>
                    <Logo className='text-white' />

                    {settings?.footerText.v && (
                        <Cms as='p' table='site_settings' id={settings.id} field={settings.footerText} className='mt-4 text-white/80' />
                    )}

                    {settings?.instagramUrl && (
                        <a
                            href={settings.instagramUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='mt-5 inline-flex size-11 items-center justify-center rounded-full border-2 border-white bg-pink text-ink'
                            aria-label='Instagram'
                        >
                            <Instagram className='size-5' />
                        </a>
                    )}
                </div>

                <div>
                    <h2 className='font-sans text-sm font-bold uppercase tracking-widest text-sun'>
                        {t('footerShop')}
                    </h2>

                    <ul className='mt-4 space-y-2.5'>
                        <li><Link href='/shop' className='hover:text-pink'>{t('navShop')}</Link></li>
                        <li><Link href='/custom-order' className='hover:text-pink'>{t('navCustom')}</Link></li>
                        <li><Link href='/cart' className='hover:text-pink'>{t('cart')}</Link></li>
                    </ul>
                </div>

                <div>
                    <h2 className='font-sans text-sm font-bold uppercase tracking-widest text-sky'>
                        {t('footerInfo')}
                    </h2>

                    <ul className='mt-4 space-y-2.5'>
                        <li><Link href='/about' className='hover:text-pink'>{t('navAbout')}</Link></li>
                        <li><Link href='/contact' className='hover:text-pink'>{t('navContact')}</Link></li>
                        <li><Link href='/shipping-and-returns' className='hover:text-pink'>{t('shippingReturns')}</Link></li>
                        <li><Link href='/privacy' className='hover:text-pink'>{t('privacy')}</Link></li>
                    </ul>
                </div>
            </div>

            <div className='border-t border-white/20'>
                <p className='mx-auto max-w-7xl px-4 py-5 text-sm text-white/60 sm:px-6'>
                    <span suppressHydrationWarning>{`© ${new Date().getFullYear()} DrawSword.`}</span>{' '}{t('rights')}
                </p>

                <p className='mx-auto max-w-7xl px-4 pb-5 text-xs text-white/50 sm:px-6'>
                    Music: Mozart, Eine kleine Nachtmusik, 1. Allegro. Recording from{' '}
                    <a href='https://commons.wikimedia.org/wiki/File:Mozart_-_Eine_kleine_Nachtmusik_-_1._Allegro.ogg' target='_blank' rel='noopener noreferrer' className='underline'>Wikimedia Commons</a>
                    , licensed{' '}
                    <a href='https://creativecommons.org/licenses/by-sa/2.0/' target='_blank' rel='noopener noreferrer' className='underline'>CC BY-SA 2.0</a>. Table Mountain photo by Dietmar Rabich,{' '}
                    <a href='https://commons.wikimedia.org/wiki/File:Cape_Town_(ZA),_Table_Mountain_--_2024_--_2794%2B96%2B98%2B2800%2B01.jpg' target='_blank' rel='noopener noreferrer' className='underline'>CC BY-SA 4.0</a>. Other South Africa photos from Wikimedia Commons (public domain).
                </p>
            </div>
        </footer>
    )
}
